import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["tabId", "onTabClick", "selectedChannelId", "trailingIcon", "disabled"],
  _excluded2 = ["allowedMessageChannelIds", "associatedObjectId", "associatedObjectTypeId", "callingSource", "currentChannelId", "defaultChannelId", "hasCalling", "isKnownVisitor", "onSelectedDropdownChange", "subjectId", "inboxId", "threadId", "tabId"];
import I18n from 'I18n';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useAsyncGenericChannelsWithCapabilities } from 'composer-data/channel-capabilities/public/hooks';
import { EMAIL_GENERIC_CHANNEL_ID, FB_MESSENGER_GENERIC_CHANNEL_ID, LIVE_CHAT_GENERIC_CHANNEL_ID, SMS_GENERIC_CHANNEL_ID, WHATSAPP_GENERIC_CHANNEL_ID } from 'conversations-message-history/common-message-format/constants/genericChannelIds';
import { isConfigKeyGenericChannelId } from 'conversations-thread-data/channel-configuration/public/operators';
import { isGenericChannelId } from 'conversations-thread-data/channel-configuration/public/types';
import { AccessLevelContextProvider } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { memo, useCallback, useEffect, useState } from 'react';
import { ChannelLabel } from '../../../capability-editor/public/components';
import { getUrlFragmentFromConfigKey } from '../../../editor-utilities/protected/utils';
import { ChannelDropdownOptions } from './ChannelDropdownOptions';
import { useSelector } from 'react-redux';
import { getIsUngatedForPersistedDrafts } from 'conversations-thread-data/auth/public/selectors';
import { usePersistentDraftsQuery } from '../../../common-reply/protected/hooks';
import { MessagesIcon, SocialFacebookMessengerIcon, EmailIcon, SocialWhatsappIcon, SmsIcon, DownCaratIcon } from 'foundations-assets';
import { Tab } from 'foundations-components/transitional/components/Tabs';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getIconForGenericChannelId(genericChannelId) {
  switch (genericChannelId) {
    case FB_MESSENGER_GENERIC_CHANNEL_ID:
      return /*#__PURE__*/_jsx(SocialFacebookMessengerIcon, {});
    case EMAIL_GENERIC_CHANNEL_ID:
      return /*#__PURE__*/_jsx(EmailIcon, {});
    case WHATSAPP_GENERIC_CHANNEL_ID:
      return /*#__PURE__*/_jsx(SocialWhatsappIcon, {});
    case SMS_GENERIC_CHANNEL_ID:
      return /*#__PURE__*/_jsx(SmsIcon, {});
    case LIVE_CHAT_GENERIC_CHANNEL_ID:
    default:
      return /*#__PURE__*/_jsx(MessagesIcon, {});
  }
}
function ChannelTab(_ref) {
  let {
      tabId,
      onTabClick,
      selectedChannelId,
      trailingIcon = null,
      disabled
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const icon = getIconForGenericChannelId(selectedChannelId);
  return /*#__PURE__*/_jsx(Tab, Object.assign({
    tabId: tabId,
    "data-test-id": "HUBSPOT-dropdown-trigger",
    "data-tab-id": tabId,
    "data-channel-id": selectedChannelId,
    disabled: disabled,
    title:
    /*#__PURE__*/
    // onClick does not work on Tab, so putting it on first element in title
    _jsxs(Flex, {
      gap: "100",
      alignItems: "center",
      onClick: onTabClick,
      "data-test-id": "channel-click",
      children: [icon, /*#__PURE__*/_jsx("span", {
        "data-test-id": "selected-channel-label",
        children: /*#__PURE__*/_jsx(ChannelLabel, {
          genericChannelId: selectedChannelId
        })
      }), trailingIcon]
    })
  }, rest));
}
ChannelTab.displayName = 'ChannelTab';
const ChannelSwitchDropdown = _ref2 => {
  let {
      allowedMessageChannelIds,
      associatedObjectId,
      associatedObjectTypeId,
      callingSource,
      currentChannelId,
      defaultChannelId,
      hasCalling,
      isKnownVisitor,
      onSelectedDropdownChange = () => {},
      subjectId,
      inboxId,
      threadId,
      tabId
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(defaultChannelId);
  const isSelected = isGenericChannelId(currentChannelId);
  const closeDropdown = useCallback(() => {
    setDropdownIsOpen(false);
  }, []);
  useEffect(() => {
    // when default channel id has changed (i.e. switching threads), reset the selected state
    setSelectedChannelId(defaultChannelId);
  }, [defaultChannelId]);
  useEffect(() => {
    if (isSelected) {
      setSelectedChannelId(currentChannelId);
    } else {
      setDropdownIsOpen(false);
    }
  }, [isSelected, currentChannelId]);

  // when switching from comments to a channel, the evt is a number || string
  // when switching channels from within the dropdown,
  // the evt will have a currentTarget & value
  const selectChannel = useCallback(evt => {
    const channel = typeof evt === 'number' || typeof evt === 'string' ? evt : evt.currentTarget && evt.currentTarget.value; // Button onClick handler using HTMLElement instead of HTMLButtonElement incorrectly

    if (!channel) return;
    const genericChannelId = isConfigKeyGenericChannelId(channel) && typeof channel === 'string' ? parseInt(channel, 10) : channel;
    closeDropdown();
    setSelectedChannelId(genericChannelId);
    const fragment = getUrlFragmentFromConfigKey({
      editorConfigKey: genericChannelId
    });
    onSelectedDropdownChange(fragment);
  }, [closeDropdown, onSelectedDropdownChange]);
  const switchOrOpenDropdown = () => {
    if (!isSelected) {
      selectChannel(selectedChannelId);
    } else {
      setDropdownIsOpen(!dropdownIsOpen);
    }
  };
  const ChannelTabWithTooltip = () => {
    const {
      loading,
      error
    } = useAsyncGenericChannelsWithCapabilities();
    const disableTab = Boolean(loading || error);
    return /*#__PURE__*/_jsx(Tooltip, {
      title: I18n.text('composer-ui.tabbed-editor.channel-switch-dropdown.tooltip-text'),
      disabled: dropdownIsOpen,
      children: /*#__PURE__*/_jsx(ChannelTab, Object.assign({
        tabId: tabId,
        onTabClick: switchOrOpenDropdown,
        selectedChannelId: selectedChannelId,
        trailingIcon: isSelected ? /*#__PURE__*/_jsx(DownCaratIcon, {
          size: "200"
        }) : null,
        disabled: disableTab
      }, rest))
    });
  };
  ChannelTabWithTooltip.displayName = 'ChannelTabWithTooltip';
  const isUngatedForPersistedDrafts = useSelector(getIsUngatedForPersistedDrafts);
  const {
    currentDrafts
  } = usePersistentDraftsQuery({
    idType: 'THREAD_ID',
    idNumber: Number(threadId),
    isUngatedForPersistedDrafts,
    fetchPolicy: 'cache-first'
  });
  return allowedMessageChannelIds.length === 1 && !hasCalling ? /*#__PURE__*/_jsx(ChannelTab, Object.assign({
    onTabClick: () => selectChannel(defaultChannelId),
    selectedChannelId: selectedChannelId,
    tabId: tabId
  }, rest)) : /*#__PURE__*/_jsx(AccessLevelContextProvider, {
    children: /*#__PURE__*/_jsx(Dropdown, {
      placement: "top",
      Button: ChannelTabWithTooltip,
      arrowSize: "small",
      animateOnToggle: true,
      closeOnMenuClick: false,
      open: dropdownIsOpen,
      caretRenderer: null,
      onOpenChange: switchOrOpenDropdown,
      children: /*#__PURE__*/_jsx(ChannelDropdownOptions, {
        allowedMessageChannelIds: allowedMessageChannelIds,
        closeDropdown: closeDropdown,
        isSelected: isSelected,
        callingSource: callingSource,
        associatedObjectId: associatedObjectId,
        associatedObjectTypeId: associatedObjectTypeId,
        hasCalling: hasCalling,
        isKnownVisitor: isKnownVisitor,
        selectChannel: selectChannel,
        subjectId: subjectId,
        inboxId: inboxId,
        threadId: threadId,
        currentDrafts: currentDrafts
      })
    })
  });
};
ChannelSwitchDropdown.displayName = 'ChannelSwitchDropdown';
export default /*#__PURE__*/memo(ChannelSwitchDropdown);